import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';

const portfolioItems = [
  {
    id: 1,
    category: 'Detailing',
    title: 'Mercedes S-Class Detailing',
    beforeImage: '/images/gallery/1-before.png',
    afterImage: '/images/gallery/1-after.png',
    description: 'Complete exterior detailing with ceramic coating',
  },
  {
    id: 2,
    category: 'Customization',
    title: 'Fortuner to Legender Conversion',
    beforeImage: '/images/gallery/2-before.png',
    afterImage: '/images/gallery/2-after.png',
    description: 'Full body kit conversion with premium paint',
  },
  {
    id: 3,
    category: 'Interior',
    title: 'BMW 7-Series Interior',
    beforeImage: '/images/gallery/3-before.png',
    afterImage: '/images/gallery/3-after.png',
    description: 'Deep cleaning and leather restoration',
  },
  {
    id: 4,
    category: 'Detailing',
    title: 'Range Rover Paint Correction',
    beforeImage: 'https://images.unsplash.com/photo-1563720360172-67b8f3dce741?w=800&q=80',
    afterImage: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800&q=80',
    description: 'Multi-stage paint correction and PPF',
  },
  {
    id: 5,
    category: 'Customization',
    title: 'Thar Off-Road Build',
    beforeImage: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800&q=80',
    afterImage: 'https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=800&q=80',
    description: 'Lift kit, wheels, and custom accessories',
  },
  {
    id: 6,
    category: 'Wheels',
    title: 'Audi RS Alloy Refurbishment',
    beforeImage: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800&q=80',
    afterImage: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&q=80',
    description: 'Powder coating and ceramic protection',
  },
  {
    id: 7,
    category: 'Detailing',
    title: 'Jaguar F-Type Polish',
    beforeImage: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&q=80',
    afterImage: 'https://images.unsplash.com/photo-1601362840469-51e4d8d58785?w=800&q=80',
    description: 'Paint correction and ceramic coating',
  },
  {
    id: 8,
    category: 'Customization',
    title: 'Defender Style Build',
    beforeImage: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80',
    afterImage: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800&q=80',
    description: 'Complete transformation with custom body kit',
  },
  {
    id: 9,
    category: 'Interior',
    title: 'Mercedes AMG Interior',
    beforeImage: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&q=80',
    afterImage: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&q=80',
    description: 'Premium interior detailing and protection',
  },
];

const categories = ['All', 'Detailing', 'Customization', 'Interior', 'Wheels'];

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAfter, setShowAfter] = useState(true);

  const filteredItems = activeCategory === 'All'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === activeCategory);

  const openLightbox = (item) => {
    setSelectedItem(item);
    setShowAfter(true);
  };

  const closeLightbox = () => {
    setSelectedItem(null);
  };

  const navigateItem = (direction) => {
    const currentIndex = filteredItems.findIndex(item => item.id === selectedItem.id);
    const newIndex = direction === 'next'
      ? (currentIndex + 1) % filteredItems.length
      : (currentIndex - 1 + filteredItems.length) % filteredItems.length;
    setSelectedItem(filteredItems[newIndex]);
  };

  return (
    <div className="pt-24 pb-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium mb-4 block">
            Our Work
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Gallery<span className="text-primary">.</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Witness the transformation. Browse our portfolio of completed builds
            and detailing masterpieces.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-primary text-black'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => openLightbox(item)}
              >
                <div className="relative aspect-square rounded-2xl overflow-hidden image-zoom">
                  <img
                    src={item.afterImage}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <span className="text-primary text-xs uppercase tracking-wider mb-2 block">
                      {item.category}
                    </span>
                    <h4 className="text-white font-semibold text-lg mb-1">{item.title}</h4>
                    <p className="text-gray-300 text-sm">{item.description}</p>
                  </div>
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                    <ZoomIn className="w-5 h-5 text-black" />
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="py-24 px-4 sm:px-6 lg:px-8 bg-[#050505]"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Want Your Vehicle Featured Here?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Join our satisfied clients. Transform your vehicle with Studio 44 Customs and become part of our portfolio of excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="btn-primary">
              Start Your Transformation
            </a>
            <a href="tel:+918179778159" className="btn-outline">
              Call Now
            </a>
          </div>
        </div>
      </motion.div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-50"
              onClick={closeLightbox}
            >
              <X className="w-6 h-6" />
            </button>

            <button
              className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-50"
              onClick={(e) => {
                e.stopPropagation();
                navigateItem('prev');
              }}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-50"
              onClick={(e) => {
                e.stopPropagation();
                navigateItem('next');
              }}
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative rounded-2xl overflow-hidden">
                <motion.img
                  key={showAfter ? 'after' : 'before'}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  src={showAfter ? selectedItem.afterImage : selectedItem.beforeImage}
                  alt={selectedItem.title}
                  className="w-full max-h-[70vh] object-contain"
                />
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div>
                  <span className="text-primary text-sm uppercase tracking-wider">
                    {selectedItem.category}
                  </span>
                  <h3 className="text-white text-2xl font-display font-bold mt-1">
                    {selectedItem.title}
                  </h3>
                  <p className="text-gray-400 mt-2">{selectedItem.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowAfter(false)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      !showAfter
                        ? 'bg-primary text-black'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    Before
                  </button>
                  <button
                    onClick={() => setShowAfter(true)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      showAfter
                        ? 'bg-primary text-black'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    After
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryPage;
